import { POSITIONS, positionLabelKey } from "../access/positions";
import { useWarehouseScope } from "../access/useWarehouseScope";
// Admin Logs — unified activity-log viewer. Backed by the Postgres
// `logs` table populated by the auto-logging middleware on the backend,
// so this page captures every user action across Portal (book-in,
// put-away, sorter, conveyor, scans, settings changes, product /
// manifest / location CRUD, admin functions, …).
//
// Server-side filtered + paginated. The table grows without bound, so
// pulling the whole set client-side and filtering in-memory (the trick
// user-list.tsx uses) isn't an option. Everything goes through query
// params and the BE returns one page at a time.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, Download, RefreshCw, Search, X } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import type { LogRow, LogsAnchor, LogsListResponse } from "../types/Log";
import { apiFetch } from "../api/apiFetch";
import "../styles/add-user.css";

// Rows-per-page options. The chosen size persists in localStorage so
// operators who prefer denser tables keep them across sessions.
const PAGE_SIZES = [50, 100, 200, 500] as const;
const PAGE_SIZE_STORAGE_KEY = "logs_page_size";
const readStoredPageSize = (): number => {
    try {
        const n = Number(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
        return (PAGE_SIZES as readonly number[]).includes(n) ? n : PAGE_SIZES[0];
    } catch { return PAGE_SIZES[0]; }
};

// YYYY-MM-DD for <input type="date"> values.
const toDateStr = (d: Date) => d.toISOString().slice(0, 10);
const todayStr = () => toDateStr(new Date());
const daysAgo = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return toDateStr(d);
};

// Date-range presets — collapses the from/to pair into a single
// dropdown so mobile viewports don't have to fit two date widgets.
// "custom" reveals the from/to inputs alongside the dropdown on
// desktop; on mobile the inputs stay hidden and "custom" is
// effectively a no-op (operator sticks with a preset).
type DatePreset = "today" | "yesterday" | "last_7" | "last_30" | "last_90" | "custom";
const DATE_PRESETS: DatePreset[] = ["today", "yesterday", "last_7", "last_30", "last_90", "custom"];
const presetRange = (p: DatePreset): { from: string; to: string } => {
    const today = todayStr();
    switch (p) {
        case "today":     return { from: today,        to: today };
        case "yesterday": { const y = daysAgo(1); return { from: y, to: y }; }
        case "last_7":    return { from: daysAgo(6),   to: today };
        case "last_30":   return { from: daysAgo(29),  to: today };
        case "last_90":   return { from: daysAgo(89),  to: today };
        default:          return { from: "",            to: "" };
    }
};

// Pre-fill the warehouse pill with the signed-in user's site so admins
// land on their own warehouse's activity by default. Mirrors the same
// trick used in user-list.tsx.
const readSignedInWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return "";
        const u = JSON.parse(raw);
        return typeof u?.warehouse === "string" ? u.warehouse : "";
    } catch { return ""; }
};

// Format `2026-05-01T11:56:42.851Z` → `01/05/26 11:56:42` in local time.
// Audit rows are dense; tight formatting keeps the table scannable.
const formatTs = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// Columns included in the CSV export. Order here is the order in the
// downloaded file. Metadata is serialised as a JSON string in its own
// column so a single log row stays a single CSV row.
const CSV_COLUMNS: { key: keyof LogRow; header: string }[] = [
    { key: "ts",            header: "Timestamp" },
    { key: "uid",           header: "UID" },
    { key: "user_name",     header: "User" },
    { key: "permissions",   header: "Permissions" },
    { key: "warehouse",     header: "Warehouse" },
    { key: "department",    header: "Department" },
    { key: "action",        header: "Action" },
    { key: "method",        header: "Method" },
    { key: "path",          header: "Path" },
    { key: "status",        header: "Status" },
    { key: "status_code",   header: "Status Code" },
    { key: "duration_ms",   header: "Duration (ms)" },
    { key: "tote_id",       header: "Tote" },
    { key: "location",      header: "Location" },
    { key: "order_no",      header: "Order" },
    { key: "po",            header: "PO" },
    { key: "sku",           header: "SKU" },
    { key: "barcode",       header: "Barcode" },
    { key: "barcode_type",  header: "Barcode Type" },
    { key: "item",          header: "Item" },
    { key: "metadata",      header: "Metadata" },
];

// RFC 4180 escaping: quote fields containing comma, quote or newline;
// double up embedded quotes. Objects (metadata) get JSON-stringified
// first so a single log row stays a single CSV row.
const csvEscape = (v: unknown): string => {
    if (v == null) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (rows: LogRow[]): string => {
    const header = CSV_COLUMNS.map(c => csvEscape(c.header)).join(",");
    const body = rows
        .map(r => CSV_COLUMNS.map(c => csvEscape(r[c.key])).join(","))
        .join("\r\n");
    return header + "\r\n" + body + (rows.length > 0 ? "\r\n" : "");
};

const downloadCsv = (filename: string, csv: string) => {
    // UTF-8 BOM so Excel detects the encoding correctly when the file
    // is opened directly without import-wizard fiddling.
    const BOM = "﻿";
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// Matches the BE page_size cap. Cap the export at 10k rows so we don't
// blow up browser memory or hammer the server — above that, ask the
// user to tighten filters. (An older BE clamps to 200 — the export
// walks pages using the size the BE echoes back, so it still completes
// correctly during a deploy window.)
const EXPORT_PAGE_SIZE = 500;
const EXPORT_MAX_ROWS = 10000;
const EXPORT_CONCURRENCY = 5;

// Render the populated entity columns as compact "key: value" chips so
// the table stays tidy regardless of which department the row came from.
// Empty / null fields are omitted entirely.
const renderContextChips = (r: LogRow): ReactNode => {
    const chips: { label: string; value: string }[] = [];
    if (r.tote_id)  chips.push({ label: "Tote",   value: r.tote_id });
    if (r.location) chips.push({ label: "Loc",    value: r.location });
    if (r.order_no) chips.push({ label: "Order",  value: r.order_no });
    if (r.po)       chips.push({ label: "PO",     value: r.po });
    if (r.sku)      chips.push({ label: "SKU",    value: r.sku });
    if (r.barcode && r.barcode !== r.sku) chips.push({ label: "Barcode", value: r.barcode });
    if (r.item)     chips.push({ label: "Item",   value: r.item });
    if (chips.length === 0) return <span className="logs-context-empty">—</span>;
    return (
        <span className="logs-context-chips">
            {/* title = full value on hover; CSS caps each chip at one
                line with an ellipsis, and the modal shows the full
                context on click. */}
            {chips.map(c => (
                <span key={c.label} className="logs-context-chip" title={c.value}>
                    <span className="logs-context-chip-label">{c.label}:</span>
                    <span className="logs-context-chip-value">{c.value}</span>
                </span>
            ))}
        </span>
    );
};

const LogsAdmin = () => {
    const { t } = useTranslation();

    // Filter state. Defaults: signed-in user's warehouse, last 7 days.
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState<string>(readSignedInWarehouse);
    // Without cross_warehouse the log is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [departmentFilter, setDepartmentFilter] = useState<string>("");
    const [userFilter, setUserFilter] = useState<string>("");
    const [actionFilter, setActionFilter] = useState<string>("");
    const [permissionFilter, setPermissionFilter] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<string>("");
    // `?search=` URL param lets AdminSearch deep-link in pre-filtered.
    // searchSubmitted is what's actually used in the BE query — only
    // updates when the operator presses Enter (or AdminSearch sets the
    // URL param). searchInput tracks the keystrokes locally so the
    // visible value updates without re-firing the request on every key.
    const [searchSubmitted, setSearchSubmitted] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState<string>(searchSubmitted);
    // Which column the search applies to. "all" → multi-column OR (slow);
    // anything else → single-column WHERE on the indexed column (fast).
    const [searchField, setSearchField] = useState<string>("all");
    const [datePreset, setDatePreset] = useState<DatePreset>("last_7");
    const [from, setFrom] = useState<string>(daysAgo(6));
    const [to, setTo]     = useState<string>(todayStr());
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState<number>(readStoredPageSize);
    // Mobile-only: search input collapses to an icon by default to save
    // row width. Toggle expands it. Ignored on desktop (CSS forces
    // visible there regardless of state).
    const [searchOpen, setSearchOpen] = useState<boolean>(false);

    // Apply a preset by overwriting from/to. "custom" leaves the
    // current values alone so the operator can hand-pick.
    const applyDatePreset = (p: DatePreset) => {
        setDatePreset(p);
        if (p === "custom") return;
        const { from: f, to: t } = presetRange(p);
        setFrom(f);
        setTo(t);
    };

    // Server response state.
    const [rows, setRows] = useState<LogRow[]>([]);
    const [total, setTotal] = useState(0);
    // The BE stops counting at 10,000 rows (an exact count of a broad view
    // took up to two minutes); past that the total reads "10,000+".
    const [totalCapped, setTotalCapped] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selected, setSelected] = useState<LogRow | null>(null);

    // Snapshot anchor for stable pagination. The first page of a view
    // returns the max log id at fetch time; echoing it back on later
    // pages pins the row set, so "next page" is exactly the rows after
    // this one even while new logs stream in — and the total only has
    // to be counted once (later pages send include_total=0 and keep
    // the cached value). A ref, not state: it feeds buildParams but
    // must not re-create load() when the response stores it (that
    // would refetch in a loop).
    const anchorRef = useRef<LogsAnchor | null>(null);

    // Filter dropdown options — driven by SELECT DISTINCT on the BE so
    // new departments / actions / users appear automatically without a
    // code change.
    const [departments, setDepartments] = useState<string[]>([]);
    const [users, setUsers]             = useState<string[]>([]);
    const [actions, setActions]         = useState<string[]>([]);
    const [warehouses, setWarehouses]   = useState<string[]>([]);

    // Reset to first page and drop the snapshot anchor on any control
    // change — the current page index is meaningless against a
    // different filter set, and a fresh view should include rows
    // written since the last anchor. (Declared before the load effect
    // so the anchor is cleared before the refetch fires.)
    useEffect(() => {
        setPage(0);
        anchorRef.current = null;
    }, [
        warehouseFilter, departmentFilter, userFilter, actionFilter,
        permissionFilter, statusFilter, searchSubmitted, searchField,
        from, to, pageSize,
    ]);

    // Action list narrows to the selected department — when the
    // department flips, drop the stale action filter so we don't end up
    // filtering by an action that doesn't exist for the new dept.
    useEffect(() => { setActionFilter(""); }, [departmentFilter]);

    // Distinct lookups for the filter dropdowns. Fetched once on mount;
    // refresh isn't needed in practice — these change only when a new
    // department/action is added, at which point a re-mount picks them up.
    useEffect(() => {
        (async () => {
            try {
                const [d, a, u] = await Promise.all([
                    apiFetch(`/admin/logs/distinct?col=department`, {}),
                    apiFetch(`/admin/logs/distinct?col=action`,     {}),
                    apiFetch(`/admin/logs/distinct?col=user_name`,  {}),
                ]);
                if (d.ok) {
                    const j = await d.json();
                    setDepartments(Array.isArray(j.values) ? j.values : []);
                }
                if (a.ok) {
                    const j = await a.json();
                    setActions(Array.isArray(j.values) ? j.values : []);
                }
                if (u.ok) {
                    const j = await u.json();
                    setUsers(Array.isArray(j.values) ? j.values : []);
                }
            } catch { /* leave empty */ }
        })();
    }, []);

    // Warehouse list comes from the existing settings endpoint — same
    // source the inventory page uses, so the available pills always
    // match the rest of the admin UI.
    useEffect(() => {
        (async () => {
            try {
                const r = await apiFetch(`/settings`, {});
                if (!r.ok) return;
                const data: { warehouse: string }[] = await r.json();
                setWarehouses(data.map(w => w.warehouse).sort());
            } catch { /* leave empty */ }
        })();
    }, []);

    const buildParams = useCallback(() => {
        const p = new URLSearchParams();
        if (warehouseFilter)  p.set("warehouse",   warehouseFilter);
        if (departmentFilter) p.set("department",  departmentFilter);
        if (userFilter)       p.set("user_name",   userFilter);
        if (actionFilter)     p.set("action",      actionFilter);
        if (permissionFilter) p.set("permissions", permissionFilter);
        if (statusFilter)     p.set("status",      statusFilter);
        if (searchSubmitted.trim()) {
            p.set("q", searchSubmitted.trim());
            // Only send search_field when narrowing — "all" is the BE
            // default and would just bloat the URL.
            if (searchField && searchField !== "all") p.set("search_field", searchField);
        }
        // Convert date-only inputs to inclusive ISO timestamps. `to` is
        // bumped to end-of-day so a row written at 23:59 still falls
        // into a "to: 2026-05-01" filter.
        if (from) p.set("from", `${from}T00:00:00.000Z`);
        if (to)   p.set("to",   `${to}T23:59:59.999Z`);
        p.set("page", String(page));
        p.set("page_size", String(pageSize));
        // Anchored view: pin the snapshot and skip the (expensive)
        // total count — we already have it from the anchoring fetch.
        if (anchorRef.current) {
            p.set("anchor_id", String(anchorRef.current.id));
            p.set("include_total", "0");
        }
        return p;
    }, [warehouseFilter, departmentFilter, userFilter, actionFilter, permissionFilter, statusFilter, searchSubmitted, searchField, from, to, page, pageSize]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiFetch(`/admin/logs?${buildParams()}`, {});
            if (r.ok) {
                const data: LogsListResponse = await r.json();
                setRows(Array.isArray(data.rows) ? data.rows : []);
                // Adopt the snapshot anchor from an anchor-establishing
                // (page-0) response. Older BEs don't send one — the page
                // then just paginates un-anchored, exactly as before.
                if (!anchorRef.current && data.anchor && typeof data.anchor.id === "number") {
                    anchorRef.current = data.anchor;
                }
                // total is null on anchored pages (include_total=0) —
                // keep the value cached from the anchoring fetch.
                if (typeof data.total === "number") {
                    setTotal(data.total);
                    setTotalCapped(data.totalCapped === true);
                }
            } else if (r.status === 401) {
                toast.error(t("logs_load_failed"), { toastId: "logs-load-err" });
            }
        } catch {
            toast.error(t("server_error"), { toastId: "logs-load-err" });
        } finally {
            setLoading(false);
        }
    }, [buildParams, t]);

    useEffect(() => { load(); }, [load]);

    // Manual refresh = drop the frozen snapshot and re-anchor at the
    // newest row. Jumping to page 0 re-triggers load via the effect;
    // when already there, call it directly.
    const refresh = useCallback(() => {
        anchorRef.current = null;
        if (page !== 0) setPage(0);
        else load();
    }, [page, load]);

    // Build the same query string load() uses, minus pagination — the
    // export will append its own page / page_size as it walks results.
    const buildExportBaseParams = useCallback(() => {
        const p = new URLSearchParams();
        if (warehouseFilter)  p.set("warehouse",   warehouseFilter);
        if (departmentFilter) p.set("department",  departmentFilter);
        if (userFilter)       p.set("user_name",   userFilter);
        if (actionFilter)     p.set("action",      actionFilter);
        if (permissionFilter) p.set("permissions", permissionFilter);
        if (statusFilter)     p.set("status",      statusFilter);
        if (searchSubmitted.trim()) {
            p.set("q", searchSubmitted.trim());
            if (searchField && searchField !== "all") p.set("search_field", searchField);
        }
        if (from) p.set("from", `${from}T00:00:00.000Z`);
        if (to)   p.set("to",   `${to}T23:59:59.999Z`);
        p.set("page_size", String(EXPORT_PAGE_SIZE));
        return p;
    }, [warehouseFilter, departmentFilter, userFilter, actionFilter, permissionFilter, statusFilter, searchSubmitted, searchField, from, to]);

    const exportFiltered = useCallback(async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const baseParams = buildExportBaseParams();

            // First page tells us the total. We then plan how many more
            // pages to pull (capped) and fetch them concurrently.
            const fetchPage = async (p: number) => {
                const params = new URLSearchParams(baseParams);
                params.set("page", String(p));
                const r = await apiFetch(`/admin/logs?${params}`, {});
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const j: LogsListResponse = await r.json();
                return j;
            };

            const first = await fetchPage(0);
            const totalAvail = typeof first.total === "number" ? first.total : 0;
            if (totalAvail === 0) {
                toast.info(t("logs_export_empty"), { toastId: "logs-export-empty" });
                return;
            }

            // Freeze the snapshot for the remaining (concurrent) pages:
            // the page-0 response carries the anchor; echoing it back
            // means rows inserted mid-export can't shift pages and
            // duplicate / drop rows in the file, and the follow-up
            // pages skip the expensive total count. Deliberately NOT
            // the table's anchorRef — an export snapshots at click
            // time, not at page-load time. Older BEs send no anchor →
            // identical to the previous behaviour.
            if (first.anchor && typeof first.anchor.id === "number") {
                baseParams.set("anchor_id", String(first.anchor.id));
                baseParams.set("include_total", "0");
            }

            const targetRows = Math.min(totalAvail, EXPORT_MAX_ROWS);
            // Walk pages at the size the BE actually served (an older
            // BE clamps 500 → 200; trusting the request size would
            // leave gaps between pages).
            const effPageSize = first.pageSize > 0 ? first.pageSize : EXPORT_PAGE_SIZE;
            const totalPages = Math.ceil(targetRows / effPageSize);
            const all: LogRow[] = Array.isArray(first.rows) ? [...first.rows] : [];

            // Pages [1 .. totalPages-1] in concurrency-limited batches.
            for (let i = 1; i < totalPages; i += EXPORT_CONCURRENCY) {
                const batch: number[] = [];
                for (let p = i; p < Math.min(i + EXPORT_CONCURRENCY, totalPages); p++) batch.push(p);
                const results = await Promise.all(batch.map(fetchPage));
                for (const r of results) {
                    if (Array.isArray(r.rows)) all.push(...r.rows);
                }
            }

            const finalRows = all.slice(0, targetRows);

            const stamp = new Date().toISOString().slice(0, 10);
            const wh = warehouseFilter ? `_${warehouseFilter.toLowerCase().replace(/\s+/g, "-")}` : "";
            downloadCsv(`portal-logs_${stamp}${wh}.csv`, buildCsv(finalRows));

            if (first.totalCapped === true || totalAvail > EXPORT_MAX_ROWS) {
                toast.warning(
                    t("logs_export_truncated", {
                        exported: String(EXPORT_MAX_ROWS),
                        total: first.totalCapped === true ? `${totalAvail}+` : String(totalAvail),
                    }),
                    { toastId: "logs-export-trunc", autoClose: 6000 }
                );
            } else {
                toast.success(
                    t("logs_export_done", { count: String(finalRows.length) }),
                    { toastId: "logs-export-done" }
                );
            }
        } catch {
            toast.error(t("logs_export_failed"), { toastId: "logs-export-err" });
        } finally {
            setExporting(false);
        }
    }, [exporting, buildExportBaseParams, warehouseFilter, t]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const rangeFrom = total === 0 ? 0 : safePage * pageSize + 1;
    const rangeTo = Math.min((safePage + 1) * pageSize, total);

    const warehouseOptions = useMemo(
        () => (warehouseScope.canCross ? warehouses : warehouseScope.own ? [warehouseScope.own] : []).map(w => ({ value: w, label: w })),
        [warehouses, warehouseScope],
    );

    return (
        <div className="logs-admin users-admin">
            {/* Mirror the Users page layout — labelled pill groups +
                search live in the same wrapper, single row that wraps
                on narrow viewports. Search sits on the LEFT so the
                primary input is reachable first; pills group on the
                right. Action / date range / refresh stay as plain
                controls — too many distinct actions for a pill row,
                and date inputs need their native widget. */}
            <div className={`users-admin-filters${searchOpen ? " logs-search-open" : ""}`}>
                {/* Search collapse on mobile: an icon button toggles
                    visibility of the input. The input itself is always
                    in the DOM so the value persists across toggles —
                    CSS hides it on mobile when .logs-search-open isn't
                    on the parent. Desktop ignores both. */}
                <button
                    type="button"
                    className="logs-action-btn logs-search-toggle"
                    onClick={() => setSearchOpen(o => !o)}
                    aria-label={t("logs_filter_search")}
                    title={t("logs_filter_search")}
                    aria-expanded={searchOpen}
                >
                    <Search size={18} />
                </button>
                {/* Search "in <column>" + input integrated into one
                    pill — the select sits on the left with rounded-left
                    corners, the input on the right with rounded-right.
                    Same pattern as <AdminSearch>. The select narrows
                    the search to one indexed column ("All" falls back
                    to the multi-column OR — slow). */}
                <div className="logs-search-wrap">
                    <select
                        className="logs-filter-searchby"
                        value={searchField}
                        onChange={e => setSearchField(e.target.value)}
                        aria-label={t("logs_search_by")}
                        title={t("logs_search_by")}
                    >
                        <option value="all">{t("logs_search_by_all")}</option>
                        <option value="user_name">{t("logs_search_by_user")}</option>
                        <option value="tote_id">{t("logs_search_by_tote")}</option>
                        <option value="location">{t("logs_search_by_location")}</option>
                        <option value="order_no">{t("logs_search_by_order")}</option>
                        <option value="po">{t("logs_search_by_po")}</option>
                        <option value="sku">{t("logs_search_by_sku")}</option>
                        <option value="item">{t("logs_search_by_item")}</option>
                        <option value="barcode">{t("logs_search_by_barcode")}</option>
                    </select>
                    <input
                        className="users-admin-filters-search"
                        type="search"
                        placeholder={t("logs_filter_search_hint")}
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                setSearchSubmitted(searchInput.trim());
                            }
                        }}
                        onBlur={() => {
                            // Commit on blur too — clears any "I typed
                            // but forgot to press Enter" confusion. Only
                            // fires when the value actually changed so
                            // the table doesn't re-query for a no-op
                            // blur.
                            if (searchInput.trim() !== searchSubmitted.trim()) {
                                setSearchSubmitted(searchInput.trim());
                            }
                        }}
                    />
                </div>
                <select
                    className="locations-table-search logs-filter-warehouse"
                    value={warehouseFilter}
                    onChange={e => setWarehouseFilter(e.target.value)}
                    disabled={!warehouseScope.canCross}
                    aria-label={t("logs_col_warehouse")}
                >
                    {warehouseScope.canCross && <option value="">{t("logs_filter_warehouse_all")}</option>}
                    {warehouseOptions.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                </select>
                <select
                    className="locations-table-search logs-filter-dept"
                    value={departmentFilter}
                    onChange={e => setDepartmentFilter(e.target.value)}
                    aria-label={t("logs_filter_department")}
                >
                    <option value="">{t("logs_filter_department_all")}</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select
                    className="locations-table-search logs-filter-user"
                    value={userFilter}
                    onChange={e => setUserFilter(e.target.value)}
                    aria-label={t("logs_filter_user")}
                >
                    <option value="">{t("logs_filter_user_all")}</option>
                    {users.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
                <select
                    className="locations-table-search logs-filter-perm"
                    value={permissionFilter}
                    onChange={e => setPermissionFilter(e.target.value)}
                    aria-label={t("logs_filter_permission")}
                >
                    <option value="">{t("logs_filter_permission_all")}</option>
                    {/* Log rows store the value the session carried at the time, so rows written
                        before the position migration say basic/admin/superAdmin and newer rows say
                        the position name. Offer both so neither set disappears from the filter. */}
                    {POSITIONS.map(p => (
                        <option key={p} value={p}>{t(positionLabelKey(p))}</option>
                    ))}
                    <option value="basic">{t("basic")} ({t("legacy_value")})</option>
                    <option value="admin">{t("admin")} ({t("legacy_value")})</option>
                    <option value="superAdmin">{t("role_super_admin")} ({t("legacy_value")})</option>
                </select>
                <select
                    className="locations-table-search logs-filter-status"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    aria-label={t("logs_col_status")}
                >
                    <option value="">{t("logs_filter_status_all")}</option>
                    <option value="success">{t("logs_status_success")}</option>
                    <option value="error">{t("logs_status_error")}</option>
                </select>
                <select
                    className="locations-table-search logs-filter-action"
                    value={actionFilter}
                    onChange={e => setActionFilter(e.target.value)}
                    aria-label={t("logs_filter_action")}
                >
                    <option value="">{t("logs_filter_action_all")}</option>
                    {actions.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                {/* Single date-range dropdown — replaces the two date
                    inputs on mobile. "Custom" reveals from/to inputs
                    (desktop only; hidden via CSS on mobile). */}
                <select
                    className="locations-table-search logs-filter-range"
                    value={datePreset}
                    onChange={e => applyDatePreset(e.target.value as DatePreset)}
                    aria-label={t("logs_filter_range")}
                >
                    {DATE_PRESETS.map(p => (
                        <option key={p} value={p}>{t(`date_preset_${p}`)}</option>
                    ))}
                </select>
                {datePreset === "custom" && (
                    <>
                        <input
                            className="locations-table-search logs-filter-date logs-filter-date-custom"
                            type="date"
                            value={from}
                            onChange={e => setFrom(e.target.value)}
                            aria-label={t("logs_filter_from")}
                        />
                        <input
                            className="locations-table-search logs-filter-date logs-filter-date-custom"
                            type="date"
                            value={to}
                            onChange={e => setTo(e.target.value)}
                            aria-label={t("logs_filter_to")}
                        />
                    </>
                )}
                <button
                    type="button"
                    className="logs-action-btn"
                    onClick={refresh}
                    aria-label={t("refresh")}
                    title={t("refresh")}
                >
                    <RefreshCw size={18} />
                </button>
                <button
                    type="button"
                    className="logs-action-btn"
                    onClick={exportFiltered}
                    disabled={exporting || loading || total === 0}
                    aria-label={t("logs_export")}
                    title={exporting ? t("logs_export_progress") : t("logs_export")}
                >
                    <Download size={18} />
                </button>
            </div>

            {loading ? (
                <div className="locations-table-empty">{t("loading")}</div>
            ) : rows.length === 0 ? (
                <div className="locations-table-empty">{t("logs_no_matches")}</div>
            ) : (
                <>
                    <div className="locations-table-wrap">
                        <table className="locations-table">
                            <thead>
                                <tr>
                                    <th>{t("logs_col_ts")}</th>
                                    <th>{t("logs_col_user")}</th>
                                    <th>{t("logs_col_department")}</th>
                                    <th>{t("logs_col_action")}</th>
                                    <th>{t("logs_col_status")}</th>
                                    <th>{t("logs_col_warehouse")}</th>
                                    <th>{t("logs_col_context")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr
                                        key={r.id}
                                        onClick={() => setSelected(r)}
                                        className="logs-row"
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td className="logs-cell-ts">{formatTs(r.ts)}</td>
                                        <td>{r.user_name ?? "—"}</td>
                                        <td>{r.department}</td>
                                        <td className="logs-cell-action">{r.action}</td>
                                        <td>
                                            <span className={`logs-status logs-status--${r.status}`}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td>{r.warehouse ?? "—"}</td>
                                        <td>{renderContextChips(r)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="locations-table-footer">
                        <div className="locations-table-footer-left">
                            <span>{t("locations_showing_range", {
                                from: String(rangeFrom),
                                to: String(rangeTo),
                                total: totalCapped ? `${total}+` : String(total),
                            })}</span>
                            {/* Rows-per-page. Changing it resets to
                                page 0 and re-anchors (via the filter-
                                reset effect — pageSize is in its deps). */}
                            <select
                                className="locations-table-search logs-page-size"
                                value={String(pageSize)}
                                onChange={e => {
                                    const n = Number(e.target.value);
                                    setPageSize(n);
                                    try { window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(n)); } catch { /* ignore */ }
                                }}
                                aria-label={t("logs_page_size")}
                                title={t("logs_page_size")}
                            >
                                {PAGE_SIZES.map(n => (
                                    <option key={n} value={String(n)}>
                                        {t("logs_page_size_option", { count: String(n) })}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="locations-table-pagination">
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={safePage === 0}
                                aria-label={t("pagination_previous")}
                            >
                                <ChevronLeft size={16} />
                                <span>{t("pagination_previous")}</span>
                            </button>
                            <span className="locations-table-page-indicator">
                                {t("pagination_page_x_of_y", {
                                    page: String(safePage + 1),
                                    total: totalCapped ? `${totalPages}+` : String(totalPages),
                                })}
                            </span>
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={safePage >= totalPages - 1}
                                aria-label={t("pagination_next")}
                            >
                                <span>{t("pagination_next")}</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {selected && (
                <LogDetailDrawer row={selected} onClose={() => setSelected(null)} t={t} />
            )}
        </div>
    );
};

// Detail modal for a single log row. Reuses the shared admin modal
// classes (.modal-overlay / .modal-card / .modal-card-header /
// .modal-close) so it slots in visually next to user-edit, user-delete,
// PrinterSelectModal, etc. — same z-index above SideNav + Header, same
// slideUp animation, same close affordance. Wider than the default
// 440px max because audit rows include path + metadata blobs.
const LogDetailDrawer = ({
    row, onClose, t,
}: {
    row: LogRow;
    onClose: () => void;
    t: (k: string, p?: Record<string, string | number>) => string;
}) => {
    const contextRows: { label: string; value: string }[] = [];
    if (row.tote_id)     contextRows.push({ label: "Tote",       value: row.tote_id });
    if (row.location)    contextRows.push({ label: "Location",   value: row.location });
    if (row.order_no)    contextRows.push({ label: "Order",      value: row.order_no });
    if (row.po)          contextRows.push({ label: "PO",         value: row.po });
    if (row.sku)         contextRows.push({ label: "SKU",        value: row.sku });
    if (row.barcode)     contextRows.push({ label: "Barcode",    value: row.barcode });
    if (row.barcode_type) contextRows.push({ label: "Type",      value: row.barcode_type });
    if (row.item)        contextRows.push({ label: "Item",       value: row.item });

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-card logs-detail-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-card-header">
                    <h2>{row.action}</h2>
                    <button className="modal-close" onClick={onClose} aria-label={t("close")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <dl className="logs-detail-grid">
                        <dt>{t("logs_col_ts")}</dt>           <dd>{formatTs(row.ts)}</dd>
                        <dt>{t("logs_col_user")}</dt>         <dd>{row.user_name ?? "—"}{row.uid != null ? ` (${row.uid})` : ""}</dd>
                        <dt>{t("logs_filter_permission")}</dt><dd>{row.permissions ?? "—"}</dd>
                        <dt>{t("logs_col_warehouse")}</dt>    <dd>{row.warehouse ?? "—"}</dd>
                        <dt>{t("logs_col_department")}</dt>   <dd>{row.department}</dd>
                        <dt>{t("logs_col_status")}</dt>
                        <dd>
                            <span className={`logs-status logs-status--${row.status}`}>{row.status}</span>
                            {row.status_code != null && <span className="logs-detail-muted"> ({row.status_code})</span>}
                        </dd>
                        <dt>{t("logs_drawer_method")}</dt>    <dd><code>{row.method}</code></dd>
                        <dt>{t("logs_drawer_path")}</dt>      <dd><code>{row.path}</code></dd>
                        <dt>{t("logs_drawer_duration")}</dt>  <dd>{row.duration_ms != null ? `${row.duration_ms} ms` : "—"}</dd>
                    </dl>

                    {contextRows.length > 0 && (
                        <>
                            <h3 className="logs-detail-subhead">{t("logs_col_context")}</h3>
                            <dl className="logs-detail-grid">
                                {contextRows.map(c => (
                                    <span key={c.label} style={{ display: "contents" }}>
                                        <dt>{c.label}</dt>
                                        <dd><code>{c.value}</code></dd>
                                    </span>
                                ))}
                            </dl>
                        </>
                    )}

                    {row.metadata && Object.keys(row.metadata).length > 0 && (
                        <>
                            <h3 className="logs-detail-subhead">{t("logs_drawer_metadata")}</h3>
                            <pre className="logs-detail-meta">{JSON.stringify(row.metadata, null, 2)}</pre>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LogsAdmin;
